import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { formatNumber } from '@/utils/formatters';

export default function useChartOptions({
  chartSeries,
  liveMode,
  yAxes,
  seriesVisibility,
  setSeriesVisibility,
}) {
  return useMemo(() => {
    const allDataTypes = Array.from(
      new Set(chartSeries.map((s) => s.dataType)),
    );
    const filteredAxes = allDataTypes.map((dt, idx) => {
      const axis = yAxes.find((a) => a.title.text === dt);
      return axis
        ? {
            ...axis,
            title: { text: '' },
            labels: {
              formatter: (point) => {
                return `${formatNumber(point.value)} ${chartSeries.find((s) => s.dataType === dt)?.base || ''}`;
              },
            },
          }
        : { title: { text: dt }, opposite: idx % 2 === 1 };
    });

    const allSeries = chartSeries.map((s) => ({
      ...s,
      visible: seriesVisibility[s.id]?.visible !== false,
      marker: { enabled: true, radius: 3 },
      yAxis: allDataTypes.indexOf(s.dataType),
    }));

    return {
      chart: { zoomType: 'x' },
      title: { text: '' },
      yAxis: filteredAxes,
      series: allSeries,
      time: { useUTC: false },
      xAxis: { type: 'datetime' },
      tooltip: {
        shared: true,
        crosshairs: true,
        formatter: function () {
          const dt = DateTime.fromMillis(this.x).toLocaleString(
            liveMode
              ? DateTime.DATETIME_MED_WITH_SECONDS
              : DateTime.DATETIME_MED,
          );
          let s = `<b>${dt}</b><br/>`;
          if (this.points) {
            this.points.forEach((point) => {
              s += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${formatNumber(point.y)}</b><br/>`;
            });
          } else {
            s += `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${formatNumber(this.y)}</b><br/>`;
          }
          return s;
        },
      },
      plotOptions: {
        series: {
          events: {
            legendItemClick: function (event) {
              event.preventDefault();
              const id = this.options.id;
              setSeriesVisibility((prev) => ({
                ...prev,
                [id]: { visible: !(prev[id]?.visible !== false) },
              }));
              return false;
            },
          },
        },
      },
      navigator: { enabled: !liveMode },
      scrollbar: { enabled: !liveMode },
    };
  }, [chartSeries, liveMode, yAxes, seriesVisibility, setSeriesVisibility]);
}
